import React, { useState, useEffect, useRef } from 'react';
import './Conditionals.css';
import CustomSelect from './CustomSelect';

const Conditionals = ({ flags = [], scales = [], endings = [], value = [], onChange, disabled = false }) => {
  const [conditionals, setConditionals] = useState(value);
  const [endingSearches, setEndingSearches] = useState({});

  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (!isInternalUpdate.current) {
      setConditionals(value);
    }
    isInternalUpdate.current = false;
  }, [value]);

  const handleConditionalsChange = (newConditionals) => {
    isInternalUpdate.current = true;
    setConditionals(newConditionals);
    if (onChange) {
      onChange(newConditionals);
    }
  };

  const addOrGroup = () => {
    const newOrGroup = {
      flags: [],
      scales: [],
      completed_endings: []
    };
    handleConditionalsChange([...conditionals, newOrGroup]);
  };

  const removeOrGroup = (groupIndex) => {
    const newConditionals = conditionals.filter((_, index) => index !== groupIndex);
    handleConditionalsChange(newConditionals);
  };

  const addFlagCondition = (groupIndex) => {
    if (flags.length === 0) return;
    
    const newCondition = {
      flag_id: flags[0].id,
      has: true
    };
    
    const newConditionals = [...conditionals];
    newConditionals[groupIndex] = {
      ...newConditionals[groupIndex],
      flags: [...newConditionals[groupIndex].flags, newCondition]
    };
    handleConditionalsChange(newConditionals);
  };

  const addScaleCondition = (groupIndex) => {
    if (scales.length === 0) return;
    
    const newCondition = {
      scale_id: scales[0].id,
      threshold: 0,
      type: 'eq'
    };
    
    const newConditionals = [...conditionals];
    newConditionals[groupIndex] = {
      ...newConditionals[groupIndex],
      scales: [...newConditionals[groupIndex].scales, newCondition]
    };
    handleConditionalsChange(newConditionals);
  };

  const removeFlagCondition = (groupIndex, conditionIndex) => {
    const newConditionals = [...conditionals];
    newConditionals[groupIndex] = {
      ...newConditionals[groupIndex],
      flags: newConditionals[groupIndex].flags.filter((_, index) => index !== conditionIndex)
    };
    handleConditionalsChange(newConditionals);
  };

  const removeScaleCondition = (groupIndex, conditionIndex) => {
    const newConditionals = [...conditionals];
    newConditionals[groupIndex] = {
      ...newConditionals[groupIndex],
      scales: newConditionals[groupIndex].scales.filter((_, index) => index !== conditionIndex)
    };
    handleConditionalsChange(newConditionals);
  };

  const updateFlagCondition = (groupIndex, conditionIndex, field, value) => {
    const newConditionals = [...conditionals];
    newConditionals[groupIndex].flags[conditionIndex] = {
      ...newConditionals[groupIndex].flags[conditionIndex],
      [field]: value
    };
    handleConditionalsChange(newConditionals);
  };

  const updateScaleCondition = (groupIndex, conditionIndex, field, value) => {
    const newConditionals = [...conditionals];
    newConditionals[groupIndex].scales[conditionIndex] = {
      ...newConditionals[groupIndex].scales[conditionIndex],
      [field]: field === 'threshold' ? parseInt(value) || 0 : value
    };
    handleConditionalsChange(newConditionals);
  };

  const updateEndingCondition = (groupIndex, conditionIndex, field, value) => {
    const newConditionals = [...conditionals];
    newConditionals[groupIndex].completed_endings[conditionIndex] = {
      ...newConditionals[groupIndex].completed_endings[conditionIndex],
      [field]: value
    };
    handleConditionalsChange(newConditionals);
  };

  const getFlagName = (flagId) => {
    const flag = flags.find(f => f.id === flagId);
    return flag ? flag.name : 'Неизвестный флаг';
  };

  const getScaleName = (scaleId) => {
    const scale = scales.find(s => s.id === scaleId);
    return scale ? scale.name : scaleId;
  };

  const getEndingName = (endingId) => {
    const ending = endings.find(e => e.id === endingId);
    return ending ? (ending.name || ending.display_name || endingId) : endingId;
  };

  const addEndingCondition = (groupIndex) => {
    if (endings.length === 0) return;
    
    const newCondition = {
      ending_id: endings[0].id,
      has: true
    };
    
    const newConditionals = [...conditionals];
    if (!newConditionals[groupIndex].completed_endings) {
      newConditionals[groupIndex].completed_endings = [];
    }
    
    newConditionals[groupIndex].completed_endings.push(newCondition);
    handleConditionalsChange(newConditionals);
  };

  const removeEndingCondition = (groupIndex, endingIndex) => {
    const newConditionals = [...conditionals];
    newConditionals[groupIndex].completed_endings.splice(endingIndex, 1);
    handleConditionalsChange(newConditionals);
  };

  return (
    <div className="conditionals-component">
      <div className="conditionals-header">
        <h4>Условия</h4>
        <p className="conditionals-description">Условия определяют когда этот элемент активен. Каждая группа ИЛИ должна иметь все свои условия выполненными (логика И). Должна быть выполнена хотя бы одна группа ИЛИ.</p>
      </div>

      {conditionals.length === 0 ? (
        <div className="no-conditionals">
          <p>Условий не установлено. Будет всегда активно.</p>
        </div>
      ) : (
        <div className="conditionals-groups">
          {conditionals.map((group, groupIndex) => (
            <div key={groupIndex} className="or-group">
              <div className="or-group-header">
                <h5>Группа ИЛИ {groupIndex + 1}</h5>
                <button
                  type="button"
                  className="remove-group-button"
                  onClick={() => removeOrGroup(groupIndex)}
                  disabled={disabled}
                >
                  Удалить группу
                </button>
              </div>

              <div className="conditions-container">
                {/* Flag Conditions */}
                {group.flags.map((condition, conditionIndex) => (
                  <div key={`flag-${conditionIndex}`} className="condition-item flag-condition">
                    <span className="condition-type">🏳️ Флаг</span>
                    <CustomSelect
                      value={condition.flag_id}
                      onChange={(e) => updateFlagCondition(groupIndex, conditionIndex, 'flag_id', e.target.value)}
                      disabled={disabled}
                      className="app-select"
                    >
                      {condition.flag_id && !flags.some(f => f.id === condition.flag_id) && (
                        <option value={condition.flag_id}><span style={{ color: '#ff6b6b' }}>Неизвестно</span></option>
                      )}
                      {flags.map(flag => (
                        <option key={flag.id} value={flag.id}>{flag.name}</option>
                      ))}
                    </CustomSelect>
                    <CustomSelect
                      value={condition.has}
                      onChange={(e) => updateFlagCondition(groupIndex, conditionIndex, 'has', e.target.value === 'true')}
                      disabled={disabled}
                      className="app-select"
                    >
                      <option value="true">Истина</option>
                      <option value="false">Ложь</option>
                    </CustomSelect>
                    <button
                      type="button"
                      className="remove-condition-button"
                      onClick={() => removeFlagCondition(groupIndex, conditionIndex)}
                      disabled={disabled}
                    >
                      Удалить
                    </button>
                  </div>
                ))}

                {/* Scale Conditions */}
                {group.scales.map((condition, conditionIndex) => (
                  <div key={`scale-${conditionIndex}`} className="condition-item scale-condition">
                    <span className="condition-type">📊 Шкала</span>
                    <CustomSelect
                      value={condition.scale_id}
                      onChange={(e) => updateScaleCondition(groupIndex, conditionIndex, 'scale_id', e.target.value)}
                      disabled={disabled}
                      className="app-select"
                    >
                      {condition.scale_id && !scales.some(s => s.id === condition.scale_id) && (
                        <option value={condition.scale_id}><span style={{ color: '#ff6b6b' }}>Неизвестно</span></option>
                      )}
                      {scales.map(scale => (
                        <option key={scale.id} value={scale.id}>{scale.name}</option>
                      ))}
                    </CustomSelect>
                    <CustomSelect
                      value={condition.type}
                      onChange={(e) => updateScaleCondition(groupIndex, conditionIndex, 'type', e.target.value)}
                      disabled={disabled}
                      className="app-select"
                    >
                      <option value="eq">Равно (=)</option>
                      <option value="gte">Больше или равно (≥)</option>
                      <option value="lte">Меньше или равно (≤)</option>
                    </CustomSelect>
                    <input
                      type="number"
                      value={condition.threshold}
                      onChange={(e) => updateScaleCondition(groupIndex, conditionIndex, 'threshold', e.target.value)}
                      disabled={disabled}
                      className="condition-input"
                      placeholder="Порог"
                    />
                    <button
                      type="button"
                      className="remove-condition-button"
                      onClick={() => removeScaleCondition(groupIndex, conditionIndex)}
                      disabled={disabled}
                    >
                      Удалить
                    </button>
                  </div>
                ))}

                {/* Ending Conditions */}
                {group.completed_endings && group.completed_endings.map((condition, endingIndex) => {
                  const searchKey = `${groupIndex}-${endingIndex}`;
                  const searchText = endingSearches[searchKey] || '';
                  const filteredEndings = searchText.trim()
                    ? endings.filter(e => e.id === condition.ending_id || (e.name || e.display_name || e.id || '').toLowerCase().includes(searchText.toLowerCase()))
                    : endings;
                  const displayEndings = filteredEndings.length > 0 ? filteredEndings : endings;
                  return (
                  <div key={`ending-${endingIndex}`} className="condition-item ending-condition">
                    <span className="condition-type">🏆 Концовка</span>
                    <input
                      type="text"
                      className="condition-ending-search"
                      placeholder="Поиск..."
                      value={searchText}
                      onChange={e => setEndingSearches(prev => ({ ...prev, [searchKey]: e.target.value }))}
                      disabled={disabled}
                    />
                    <CustomSelect
                      value={condition.ending_id}
                      onChange={(e) => updateEndingCondition(groupIndex, endingIndex, 'ending_id', e.target.value)}
                      disabled={disabled}
                      className="app-select"
                    >
                      {condition.ending_id && !endings.some(e => e.id === condition.ending_id) && (
                        <option value={condition.ending_id}><span style={{ color: '#ff6b6b' }}>Неизвестно</span></option>
                      )}
                      {displayEndings.map(ending => (
                        <option key={ending.id} value={ending.id}>{ending.name || ending.display_name || ending.id}</option>
                      ))}
                    </CustomSelect>
                    <CustomSelect
                      value={condition.has}
                      onChange={(e) => updateEndingCondition(groupIndex, endingIndex, 'has', e.target.value === 'true')}
                      disabled={disabled}
                      className="app-select"
                    >
                      <option value="true">Истина</option>
                      <option value="false">Ложь</option>
                    </CustomSelect>
                    <button
                      type="button"
                      className="remove-condition-button"
                      onClick={() => removeEndingCondition(groupIndex, endingIndex)}
                      disabled={disabled}
                    >
                      Удалить
                    </button>
                  </div>
                  );
                })}

                {/* Add Condition Buttons */}
                <div className="add-condition-buttons">
                  {flags.length > 0 && (
                    <button
                      type="button"
                      className="add-condition-button flag"
                      onClick={() => addFlagCondition(groupIndex)}
                      disabled={disabled}
                    >
                      🏳️ + Флаг
                    </button>
                  )}
                  {scales.length > 0 && (
                    <button
                      type="button"
                      className="add-condition-button scale"
                      onClick={() => addScaleCondition(groupIndex)}
                      disabled={disabled}
                    >
                      📊 + Шкала
                    </button>
                  )}
                  {endings.length > 0 && (
                    <button
                      type="button"
                      className="add-condition-button ending"
                      onClick={() => addEndingCondition(groupIndex)}
                      disabled={disabled}
                    >
                      🏆 + Концовка
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="conditionals-controls">
        <button
          type="button"
          className="add-or-group-button"
          onClick={addOrGroup}
          disabled={disabled}
        >
          ➕ Добавить группу ИЛИ
        </button>
      </div>
    </div>
  );
};

export default Conditionals; 